import React, { useState, useEffect } from "react";
import { Carousel, CarouselItem, Badge } from "reactstrap";
import { useSwipeable } from "react-swipeable";
import sailfish from "../../assets/images/fish_types/sailfish.png";
import yellowFinTuna from "../../assets/images/fish_types/yellowfin_tuna.png";
import swordFish from "../../assets/images/fish_types/sword_fish.png";
import bigEyeTuna from "../../assets/images/fish_types/big_eye_tuna.png";
import blackMarlin from "../../assets/images/fish_types/blackmarlin.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { getStock } from "../../api/stock/getStockApi.js";
import { Container } from 'reactstrap';

// Mapping from UI display names (items.content) to API Product_Name
const productNameMapping = {
  Tuna: 'Tuna',
  'Bigeye Tuna': 'Bigeye Tuna',
  Sailfish: 'Sailfish',
  'Black Marlin': 'Black Marlin',
  'Yellowfin Tuna': 'Yellowfin Tuna',
  Swordfish: 'Swordfish',
  'Bullet-tuna': 'Bullet-tuna',
  'Southern-bluefin-tuna': 'Southern-bluefin-tuna',
  'Kawa-kawa': 'Kawa-kawa',
};

const items = [
  { id: 1, img: yellowFinTuna, content: 'Tuna' },
  { id: 2, img: bigEyeTuna, content: 'Bigeye Tuna' },
  { id: 3, img: sailfish, content: 'Sailfish' },
  { id: 4, img: blackMarlin, content: 'Black Marlin' },
  { id: 5, img: yellowFinTuna, content: 'Yellowfin Tuna' },
  { id: 6, img: swordFish, content: 'Swordfish' },
  { id: 7, img: bigEyeTuna, content: 'Bullet-tuna' },
  { id: 8, img: swordFish, content: 'Southern-bluefin-tuna' },
  { id: 9, img: swordFish, content: 'Kawa-kawa' },
];

const ResponsiveCarousel = ({
  showOutOfStockMessage,
  setShowOutOfStockMessage,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [itemsPerSlide, setItemsPerSlide] = useState(6);
  const [stockCounts, setStockCounts] = useState({});
  const navigate = useNavigate();

  // Fetch stock data and calculate counts
  useEffect(() => {
    const fetchStockData = async () => {
      try {
        const response = await getStock();
        const stockData = response.data.data; // Access the 'data' array
        console.log('Stock Data:', stockData);
        if (!Array.isArray(stockData)) {
          throw new Error('Expected stockData to be an array');
        }
        const counts = stockData.reduce((acc, item) => {
          const productName = item.Product_Name || 'Unknown'; // Fallback for missing Product_Name
          const quantity = parseInt(item.Total_Available_Quantity, 10) || 0;
          console.log('Processing:', { productName, quantity });
          acc[productName] = (acc[productName] || 0) + quantity;
          return acc;
        }, {});
        console.log('Computed Stock Counts:', counts);
        setStockCounts(counts);
      } catch (error) {
        console.error('Error fetching stock data:', error);
        setStockCounts({});
      }
    };
    fetchStockData();
  }, []);

  // Update items per slide based on window size
  useEffect(() => {
    const updateItemsPerSlide = () => {
      const width = window.innerWidth;
      if (width < 576) setItemsPerSlide(2);
      else if (width < 768) setItemsPerSlide(3);
      else if (width < 992) setItemsPerSlide(4);
      else setItemsPerSlide(8);
    };

    updateItemsPerSlide();
    window.addEventListener('resize', updateItemsPerSlide);
    return () => window.removeEventListener('resize', updateItemsPerSlide);
  }, []);

  // Display all items, no filtering
  const displayItems = items; // Always show all items
  console.log('Display Items:', displayItems);

  const chunkedItems = displayItems.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / itemsPerSlide);
    if (!resultArray[chunkIndex]) resultArray[chunkIndex] = [];
    resultArray[chunkIndex].push(item);
    return resultArray;
  }, []);

  const next = () => {
    if (animating) return;
    setActiveIndex(
      activeIndex === chunkedItems.length - 1 ? 0 : activeIndex + 1
    );
  };

  const previous = () => {
    if (animating) return;
    setActiveIndex(
      activeIndex === 0 ? chunkedItems.length - 1 : activeIndex - 1
    );
  };

  const handleClick = (item) => {
    try {
      const expirationTime = Date.now() + 10000; // 10 seconds from now
      const storageItem = {
        value: {
          id: item.id,
          content: item.content,
          timestamp: new Date().toISOString(),
        },
        expiry: expirationTime,
      };

      localStorage.setItem(
        `carouselItem_${item.id}`,
        JSON.stringify(storageItem)
      );
      // Set out-of-stock message if the selected fish type has no stock
      const apiProductName = productNameMapping[item.content];
      if (!stockCounts[apiProductName] || stockCounts[apiProductName] === 0) {
        setShowOutOfStockMessage(true);
      } else {
        setShowOutOfStockMessage(false);
      }
      navigate('/stocks');
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      navigate('/stocks'); // Navigate even if localStorage fails
    }
  };

  const slides = chunkedItems.map((chunk, index) => (
    <CarouselItem
      key={index}
      onExiting={() => setAnimating(true)}
      onExited={() => setAnimating(false)}
    >
      <div className="d-flex">
        {chunk.map((item) => {
          const apiProductName = productNameMapping[item.content];
          const isAvailable = stockCounts[apiProductName] > 0;
          return (
            <div
              key={item.id}
              className={`item-card text-center border p-4 m-4 hover-effect ${
                !isAvailable ? '' : ''
              }`}
              style={{
                width: `${100 / itemsPerSlide}%`,
                borderRadius: '10px',
                position: 'relative',
                overflow: 'hidden',
                opacity: isAvailable ? 1 : 1, // Dim unavailable items
              }}
              onClick={() => handleClick(item)}
            >
              <div className="hover-bg"></div>
              <img
                src={item.img}
                alt={item.content}
                className="w-1 my-2 mx-auto"
              />
              <h6 className="card-text">
                {item.content} <br />
                {isAvailable ? (
                  `(${stockCounts[apiProductName]} Kg)`
                ) : (
                  <Badge color="warning mt-2">Out Of Stock</Badge>
                )}
              </h6>
            </div>
          );
        })}
      </div>
    </CarouselItem>
  ));

  const swipeHandlers = useSwipeable({
    onSwipedLeft: next,
    onSwipedRight: previous,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  return (
    <React.Fragment>
      <section className="bg-light gaps">
        <Container fluid>
          <div className=" row d-flex align-items-center">
            <div className="col-md-8 no-margin">
              <h4 className="subtitle">Featured Categories</h4>
            </div>
            <div className="col-md-4 d-flex justify-content-end">
              <button
                onClick={previous}
                className="btn1 mx-1 carousel-control-prev1"
                aria-label="Previous"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <button
                onClick={next}
                className="btn1 mx-1 carousel-control-next1"
                aria-label="Next"
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>
          <div {...swipeHandlers}>
            <Carousel
              className="carousel-fish-list"
              activeIndex={activeIndex}
              next={next}
              previous={previous}
            >
              {slides}
            </Carousel>
          </div>
        </Container>
      </section>
    </React.Fragment>
  );
};

export default ResponsiveCarousel;
